import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Map, Marker, ZoomControl, Overlay } from "pigeon-maps";
import { useStaticQuery, graphql, navigate } from "gatsby";
import MapStyleSwitcher from "./MapStyleSwitcher"; 
import CountryFlagMarker from "./CountryFlagMarker"; // Import the new marker component
import PointMarker from "./PointMarker"; // Import PointMarker component
import PostMarker from "./PostMarker"; // Import PostMarker component


import { buildCategoryPath } from "../../utils/path-utils";
import { hasFlagAsset } from "../../utils/flag-assets";

// Mapbox Access and Style URL
const GATSBY_MAPBOX_ACCESS_TOKEN = process.env.GATSBY_MAPBOX_ACCESS_TOKEN || "";
const GATSBY_MAPBOX_URL = "lececil123/cm1quujef00gt01pl4616glxy";

export interface MapState {
  center: [number, number];
  zoom: number;
}

export interface MapContainerProps {
  mapState: MapState;
  setMapState: React.Dispatch<React.SetStateAction<MapState>>;
  selectedLocation?: { lat: number; lng: number };
  resetToDefault?: boolean;
}

const getMapProvider =
  (mapStyle: string) => (x: number, y: number, z: number, dpr: number) =>
    `https://api.mapbox.com/styles/v1/${mapStyle}/tiles/512/${z}/${x}/${y}${
      dpr >= 2 ? "@2x" : ""
    }?access_token=${GATSBY_MAPBOX_ACCESS_TOKEN}`;

const filterOptions = [
  { key: "posts", label: "Places", color: "black" },
  { key: "beach", label: "Beach", color: "orange" },
  { key: "climbing", label: "Climbing", color: "brown" },
  { key: "biking", label: "Biking", color: "DarkGreen" },
  { key: "parking", label: "Airport", color: "red" },
  { key: "park", label: "Park", color: "DarkOliveGreen" },
  { key: "mountain", label: "Mountain", color: "DarkSlateGrey" },
  { key: "camping", label: "Camping", color: "DarkGrey" },
  { key: "lake", label: "Lake", color: "blue" },
  { key: "others", label: "Others", color: "gray" },
  { key: "categories", label: "Categories", color: "purple" },
];


const MapContainer: React.FC<MapContainerProps> = ({
  mapState,
  setMapState,
  selectedLocation,
  resetToDefault = false,
}) => {
  
  const { center, zoom } = mapState;
  const [mapStyle, setMapStyle] = useState(GATSBY_MAPBOX_URL);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredMarker, setHoveredMarker] = useState<any>(null);
  const [filters, setFilters] = useState<{ [key: string]: boolean }>({
    posts: true,
    beach: true,
    climbing: true,
    biking: true,
    parking: true,
    park: true,
    mountain: true,
    camping: true,
    lake: true,
    others: true,
    categories: true,
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeCategorySlug, setActiveCategorySlug] = useState<string | null>(null);
  const [activeTagSlug, setActiveTagSlug] = useState<string | null>(null);
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';

  // Effect to get active category and tag from URL
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      let categorySlug: string | null = null;
      let tagSlug: string | null = null;

      // Check for /category/slug/tag/tag-slug or /slug/tag/tag-slug
      const tagIndex = pathParts.indexOf('tag');
      if (tagIndex !== -1 && pathParts.length > tagIndex + 1) {
        tagSlug = pathParts[tagIndex + 1];
        // If tag is present, check if there's a category before it
        if (tagIndex > 0) {
          categorySlug = pathParts[tagIndex - 1];
          if (categorySlug === 'category' && tagIndex > 1) {
            categorySlug = pathParts[tagIndex - 2];
          }
        }
      } else if (pathParts.length > 0) {
        // Check for /category/slug or /slug
        if (pathParts[0] === 'category' && pathParts.length > 1) {
          categorySlug = pathParts[1];
        } else if (pathParts[0] !== 'tag' && pathParts[0] !== 'page') { // Avoid /tag/ and /page/
          categorySlug = pathParts[0];
        }
      }

      setActiveCategorySlug(categorySlug);
      setActiveTagSlug(tagSlug);
    }
  }, [currentPath]); // Re-run when pathname changes

  // Detect system dark mode and switch basemap style automatically
useEffect(() => {
  if (typeof window !== 'undefined') {
    const darkMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const updateMapStyle = (isDark: boolean) => {
      const darkStyle = "lececil123/cm6kvyvhi00ia01qr7ajegmwo"; // Your dark Mapbox style
      const lightStyle = "lececil123/cm1quujef00gt01pl4616glxy"; // Your current light style
      setMapStyle(isDark ? darkStyle : lightStyle);
    };

    // Set initial mode
    updateMapStyle(darkMediaQuery.matches);

    // Listen for changes
    const changeHandler = (e: MediaQueryListEvent) => updateMapStyle(e.matches);
    darkMediaQuery.addEventListener("change", changeHandler);

    // Cleanup listener
    return () => {
      darkMediaQuery.removeEventListener("change", changeHandler);
    };
  }
}, []);


  // Load user location from localStorage
  useEffect(() => {
    const storedLocation = localStorage.getItem("userLocation");
    if (storedLocation) {
      try {
        setUserLocation(JSON.parse(storedLocation));
      } catch (err) {
        console.error("Error parsing user location from localStorage", err);
      }
    }
    setLoading(false);
  }, []);

  // Reset map to default if requested
  useEffect(() => {
    if (resetToDefault) setMapState({ center: [50.879, 2.6997], zoom: 3 });
  }, [resetToDefault, setMapState]);

  // Center on selected location
  useEffect(() => {
    if (selectedLocation?.lat && selectedLocation?.lng) {
      setMapState({ center: [selectedLocation.lat, selectedLocation.lng], zoom: selectedLocation.zoom || 10 });
    }
  }, [selectedLocation, setMapState]);

  const data = useStaticQuery(graphql`
    query MapPostsAndEventsQuery {
      allWpPost {
        nodes {
          id
          title
          uri
          categories {
            nodes {
              slug
              databaseId
              parentDatabaseId
            }
          }
          location {
            mapLatitude
            mapLongitude
          }
          tags {
            nodes {
              slug
            }
          }
        }
      }
      allWpEvent {
        nodes {
          id
          title
          uri
          location {
            mapLatitude
            mapLongitude
          }
        }
      }
      allWpPoint {
        nodes {
          id
          title
          slug
          categories {
            nodes {
              slug
              databaseId
              parentDatabaseId
            }
          }
          pointType {
            nodes {
              slug
            }
          }
          location {
            mapLatitude
            mapLongitude
          }
          tags {
            nodes {
              slug
            }
          }
        }
      }
      allWpCategory {
        nodes {
          slug
          databaseId
          parent {
            id
          }
          parentDatabaseId
          parentId
          name
          description
          location {
            mapLatitude
            mapLongitude
          }
          uri
          link
          categoryId
        }
      }
    }
  `);

  const handleMarkerClick = useCallback(
    (lat: number, lng: number, url: string) => {
      if (currentPath === url) {
        // If the clicked marker is already the active one, do nothing.
        return;
      }
      setMapState({ center: [lat, lng] });
      setHoveredMarker(null); // Reset hovered marker
      setTimeout(() => navigate(url), 300);
    },
    [setMapState, currentPath]
  );

  const handleMarkerMouseOver = useCallback((node: any) => setHoveredMarker(node), []);
  const handleMarkerMouseOut = useCallback(() => setHoveredMarker(null), []);

  const handleUseMyLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location: [number, number] = [latitude, longitude];
          setUserLocation(location);
          setMapState({ center: location, zoom: 10 });
          localStorage.setItem("userLocation", JSON.stringify(location));
        },
        (error) => console.error("Error fetching user location", error)
      );
    }
  }, [setMapState]);

const allCategories = data.allWpCategory.nodes;

const getPostUrl = (node: any) => {
  const primaryCategory = node.categories?.nodes?.[0];
  const categoryPath = primaryCategory ? buildCategoryPath(primaryCategory, allCategories) : '';
  let safeUri = node.uri ? node.uri : `/${node.slug}`;
  if (!safeUri.startsWith('/')) safeUri = `/${safeUri}`;
  if (!safeUri.endsWith('/')) safeUri = `${safeUri}/`;
  const cleanedUri = safeUri.replace(/\/point(\/|$)/g, '/').replace(/\/+/g, '/');
  const postUrl = `/${categoryPath}${cleanedUri}`.replace(/\/+/g, '/');
  return postUrl;
};

const getPointUrl = (node: any) => {
  const primaryCategory = node.categories?.nodes?.[0];
  const categoryPath = primaryCategory ? buildCategoryPath(primaryCategory, allCategories) : '';
  let safeUri = node.uri ? node.uri : `/${node.slug}`;
  if (!safeUri.startsWith('/')) safeUri = `/${safeUri}`;
  if (!safeUri.endsWith('/')) safeUri = `${safeUri}/`;
  const cleanedUri = safeUri.replace(/\/point(\/|$)/g, '/').replace(/\/+/g, '/');
  const postUrl = `/${categoryPath}${cleanedUri}`.replace(/\/+/g, '/');
  return postUrl;
};

  const activeNode = useMemo(() => {
    let foundNode = null;
    // Check posts
    for (const node of data.allWpPost.nodes) {
      if (getPostUrl(node) === currentPath) {
        foundNode = { ...node, type: 'post' };
        break;
      }
    }
    // Check points if not found in posts
    if (!foundNode) {
      for (const node of data.allWpPoint.nodes) {
        if (getPointUrl(node) === currentPath) {
          foundNode = { ...node, type: 'point' };
          break;
        }
      }
    }
    return foundNode;
  }, [currentPath, data.allWpPost.nodes, data.allWpPoint.nodes, allCategories]); // Add allCategories to dependencies

  const activeNodeId = activeNode?.id;
  const activeNodeType = activeNode?.type;

  const allMarkers = useMemo(() => {
    const markers = [];

    // Add posts
    if (mapState.zoom > 5 && filters.posts) {
      data.allWpPost.nodes.forEach((node: any) => {
        const { mapLatitude, mapLongitude } = node.location || {};
        if (!mapLatitude || !mapLongitude) return;

        const postCategorySlugs = node.categories?.nodes?.map((cat: any) => cat.slug) || [];
        const postTagSlugs = node.tags?.nodes?.map((tag: any) => tag.slug) || [];

        let isCurrentCategory = true;
        if (activeCategorySlug) {
          isCurrentCategory = postCategorySlugs.includes(activeCategorySlug);
        }

        let isCurrentTag = true;
        if (activeTagSlug) {
          isCurrentTag = postTagSlugs.includes(activeTagSlug);
        }

        const isCategoryOrTagSelected = isCurrentCategory && isCurrentTag;
        const isActive = node.id === activeNodeId;

        markers.push({
          id: node.id,
          type: 'post',
          isCurrent: isCategoryOrTagSelected || isActive,
          anchor: [parseFloat(mapLatitude), parseFloat(mapLongitude)],
          width: isCategoryOrTagSelected ? 28 : 16,
                    zIndex: isActive ? 1000 : 2,          onClick: () => handleMarkerClick(parseFloat(mapLatitude), parseFloat(mapLongitude), getPostUrl(node)),
          onMouseOver: () => handleMarkerMouseOver(node),
          onMouseOut: handleMarkerMouseOut,
          children: (
            <PostMarker
              onClick={() => handleMarkerClick(parseFloat(mapLatitude), parseFloat(mapLongitude), getPostUrl(node))}
              onMouseOver={() => handleMarkerMouseOver(node)}
              onMouseOut={handleMarkerMouseOut}
              title={node.title}
              isCategoryOrTagSelected={isCategoryOrTagSelected}
              isActive={isActive}
            />
          ),
        });
      });
    }

    // Add events
    if (mapState.zoom > 5) {
      data.allWpEvent.nodes.forEach((node: any) => {
        const { mapLatitude, mapLongitude } = node.location || {};
        if (!mapLatitude || !mapLongitude) return;

        markers.push({
          id: node.id,
          type: 'event',
          isCurrent: false, // Events don't have current state
          anchor: [parseFloat(mapLatitude), parseFloat(mapLongitude)],
          width: 33,
          color: '#d90d0d',
          onClick: () => handleMarkerClick(parseFloat(mapLatitude), parseFloat(mapLongitude), node.uri),
          onMouseOver: () => handleMarkerMouseOver(node),
          onMouseOut: handleMarkerMouseOut,
        });
      });
    }

    // Add points
    if (mapState.zoom > 6) {
      data.allWpPoint.nodes.forEach((node: any) => {
        const { mapLatitude, mapLongitude } = node.location || {};
        if (!mapLatitude || !mapLongitude) return;

        const pointCategorySlugs = node.categories?.nodes?.map((cat: any) => cat.slug) || [];
        const pointTagSlugs = node.tags?.nodes?.map((tag: any) => tag.slug) || [];

        let isCurrentCategory = true;
        if (activeCategorySlug) {
          isCurrentCategory = pointCategorySlugs.includes(activeCategorySlug);
        }

        let isCurrentTag = true;
        if (activeTagSlug) {
          isCurrentTag = pointTagSlugs.includes(activeTagSlug);
        }

        const isCategoryOrTagSelected = isCurrentCategory && isCurrentTag;
        const isActive = node.id === activeNodeId;

        const types = node.pointType?.nodes?.map((t: any) => t.slug.toLowerCase()) || [];
        const knownTypes = ["beach", "climbing", "biking", "park", "mountains", "camping", "airport", "lake"];
        const isBeach = types.includes("beach");
        const isParking = types.includes("airport");
        const isClimbing = types.includes("climbing");
        const isPark = types.includes("park");
        const isMountain = types.includes("mountains");
        const isCamping = types.includes("camping");
        const isLake = types.includes("lake");
        const isBiking = types.includes("biking");
        const isKnown = types.some((t) => knownTypes.includes(t));

        if (
          (isBeach && !filters.beach) ||
          (isParking && !filters.parking) ||
          (isClimbing && !filters.climbing) ||
          (isBiking && !filters.biking) ||
          (isPark && !filters.park) ||
          (isMountain && !filters.mountain) ||
          (isCamping && !filters.camping) ||
          (isLake && !filters.lake) ||
          (isBiking && !filters.biking) ||
          (!isKnown && !filters.others)
        ) return;

        const color = isBeach
          ? "orange"
          : isParking
          ? "darkred"
          : isClimbing
          ? "brown"
          : isBiking
          ? "DarkGreen"
          : isPark
          ? "DarkOliveGreen"
          : isMountain
          ? "DarkSlateGrey"
          : isCamping
          ? "DarkGrey"
          : isLake
          ? "blue"
          : "gray";



        const currentPointType = isBeach
          ? "beach"
          : isParking
          ? "airport"
          : isClimbing
          ? "climbing"
          : isBiking
          ? "biking"
          : isPark
          ? "park"
          : isMountain
          ? "mountains"
          : isCamping
          ? "camping"
          : isLake
          ? "lake"
          : "others";

        markers.push({
          id: node.id,
          type: 'point',
          isCurrent: isCategoryOrTagSelected || isActive,
          anchor: [parseFloat(mapLatitude), parseFloat(mapLongitude)],
          width: isCategoryOrTagSelected ? 28 : 16,
          color,
          zIndex: isActive ? 1000 : 1,
          onClick: () => handleMarkerClick(parseFloat(mapLatitude), parseFloat(mapLongitude), getPointUrl(node)),
          onMouseOver: () => handleMarkerMouseOver(node),
          onMouseOut: handleMarkerMouseOut,
          children: (
            <PointMarker
              type={currentPointType}
              onClick={() => handleMarkerClick(parseFloat(mapLatitude), parseFloat(mapLongitude), getPointUrl(node))}
              onMouseOver={() => handleMarkerMouseOver(node)}
              onMouseOut={handleMarkerMouseOut}
              title={node.title}
              isCategoryOrTagSelected={isCategoryOrTagSelected}
              isActive={isActive}
            />
          ),
        });
      });
    }

    // Sort markers to bring current ones to the front
    return markers.sort((a, b) => (a.isCurrent ? 1 : -1));

  }, [mapState.zoom, filters, activeCategorySlug, activeTagSlug, data, currentPath]);
  const renderMarkersCategory = () => {
    if (mapState.zoom <= 5 && filters.categories) {
      const markersToRender = data.allWpCategory.nodes.map((node: any) => {
        const { mapLatitude, mapLongitude } = node.location || {};
        if (!mapLatitude || !mapLongitude) {
          return null;
        }

        // Only show markers that have a real flag asset (regions like Algarve stay hidden)
        if (!hasFlagAsset(node.slug)) {
          return null;
        }

        return (
          <Marker
            key={node.categoryId}
            anchor={[parseFloat(mapLatitude), parseFloat(mapLongitude)]}
            onClick={() =>
              handleMarkerClick(parseFloat(mapLatitude), parseFloat(mapLongitude), node.uri.replace("/category", ""))
            }
            onMouseOver={() => handleMarkerMouseOver({ ...node, title: node.name })}
            onMouseOut={handleMarkerMouseOut}
          >
            <CountryFlagMarker
              countryCode={node.slug}
              onClick={() =>
                handleMarkerClick(parseFloat(mapLatitude), parseFloat(mapLongitude), node.uri.replace("/category", ""))
              }
              onMouseOver={() => handleMarkerMouseOver({ ...node, title: node.name })}
              onMouseOut={handleMarkerMouseOut}
            />
          </Marker>
        );
      }).filter(Boolean);
      return markersToRender;
    }
    return null;
  };

  if (loading) {return (
      <div className="loadingContainer">
        <div className="spinner"></div>
        <span className="loadingText">Loading map...</span>
      </div>
    );
  }

  return (
    <div>
      <button
        className="location-button"
        tabIndex={-1}
        title="Get your location"
        aria-label="Get your location"
        onClick={handleUseMyLocation}
      >
        <span>My location</span>
      </button>

      <MapStyleSwitcher onStyleChange={setMapStyle} />

      <div
        style={{ position: "absolute", top: "10px", left: "10px", zIndex: 1000 }}
        onMouseLeave={() => setFilterOpen(false)}
      >
        <button
          onClick={() => setFilterOpen((prev) => !prev)}
          style={{
            background: "rgba(255,255,255,0.9)",
            border: "1px solid #ccc",
            borderRadius: "8px",
            padding: "6px 10px",
            cursor: "pointer",
            fontSize: "12px",
          }}
        >
          {filterOpen ? "Close ✖" : "Layers ☰"}
        </button>
        {filterOpen && (
          <div
            style={{
              marginTop: "6px",
              backgroundColor: "rgba(255,255,255,0.95)",
              padding: "8px",
              borderRadius: "8px",
              fontSize: "12px",
              display: "grid",
              justifyItems: "start",
            }}
          >
            {filterOptions.map(({ key, label, color }) => (
              <label key={key} style={{ display: "flex", alignItems: "center", marginBottom: 4, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={filters[key]}
                  onChange={(e) => setFilters((prev) => ({ ...prev, [key]: e.target.checked }))}
                  style={{ accentColor: color }}
                />
                <img src={`/images/icons/${label}.svg`} style={{ width: "30px", paddingRight: "0px"}}/><span style={{ color, marginLeft: 4 }}>{label}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <Map
        provider={getMapProvider(mapStyle)}
        dprs={[1, 2]}
        center={center}
        zoom={zoom}
        zoomSnap={false}
        animate
        touchEvents
        twoFingerDrag={false}
        onBoundsChanged={({ center, zoom }) => setMapState({ center, zoom })}
        style={{ width: "100%", minHeight: "500px" }}
      >
        {allMarkers.map(marker => (
          <Marker key={marker.id} {...marker} style={{ zIndex: marker.zIndex || 1 }}>
            {marker.children}
          </Marker>
        ))}

        {filters.categories && renderMarkersCategory()}

        {userLocation && <Marker anchor={userLocation} width={50} hover={false} color="lightblue" style={{ zIndex: 999 }} onClick={() => navigate("/places-around-me/")} />}

        {hoveredMarker && (
          <Overlay
            anchor={[
              parseFloat(hoveredMarker.location.mapLatitude),
              parseFloat(hoveredMarker.location.mapLongitude),
            ]}
            offset={[0, -10]}
            zIndex={9999} // Ensure overlay is on top
          >
            <div
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                padding: "5px",
                borderRadius: "5px",
                maxWidth: "200px",
              }}
            >
              {hoveredMarker.title && hoveredMarker.title.trim() !== '' ? hoveredMarker.title.split(":")[0] : hoveredMarker.name}
            </div>
          </Overlay>
        )}

        <ZoomControl style={{ bottom: "10px", top: "unset", zoom: "1.4" }} />
      </Map>
    </div>
  );
};

export default MapContainer;
